import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject, _templateObject2, _templateObject3, _templateObject4;

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Tooltip from './Tooltip';
import { colors } from '@vds/theme';
import { calculateRem } from '@uie/javascript-style-helpers';
var propTypes = {
  /**
   * Content that will be passed to the typography.
   */
  children: PropTypes.node,

  /**
   * VDS Typography component that is used to render the text preceding the tooltip.
   */
  typographyType: PropTypes.oneOf(['title', 'subtitle', 'body', 'micro']),

  /**
   * Primitive used for the typgraphy.
   */
  typographyPrimitive: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'p']),

  /**
   * Viewport the font styling is based on. Desktop and tablet are the same size.
   */
  viewport: PropTypes.oneOf(['desktop', 'tablet', 'mobile']),

  /**
   * Size of the typography component to be rendered.
   */
  typographySize: PropTypes.string,

  /**
   * Color of the typography component to be rendered.
   */
  typographyColor: PropTypes.string,

  /**
   * Adds a fontWeight of bold to the typography component, only valid for Body and Title typographyTypes.
   */
  bold: PropTypes.bool,

  /**
   * Determines the size of the tooltip.
   */
  tooltipSize: PropTypes.oneOf(['small', 'large']),

  /**
   * Determines the content of the tooltip.
   */
  tooltipContent: PropTypes.any,

  /**
   * Determines the title of the tooltip.
   */
  tooltipTitle: PropTypes.string,

  /**
   * If passed, component will render in the inverted state.
   */
  inverted: PropTypes.bool,

  /**
   * Allows a unique ID to be passed to the component.
   */
  id: PropTypes.string,

  /**
   * Allows a unique component ID to be passed to the component as a reference for positioning other than the window object.
   */
  containerId: PropTypes.string,

  /**
   * A String label that is required for accessibility, the should be very terse description ot the tooltips content.
   */
  ariaLabel: PropTypes.string,

  /**
   * @ignore Passes in VDS Body component.
   */
  Body: PropTypes.func,

  /**
   * @ignore Passes in VDS Body component.
   */
  Micro: PropTypes.func,

  /**
   * @ignore Passes in VDS Body component.
   */
  Title: PropTypes.func,

  /**
   * @ignore Passes in VDS Body component.
   */
  Subtitle: PropTypes.func,

  /**
   * @ignore Passes in VDS Body font configuration.
   */
  bodyConfig: PropTypes.object,

  /**
   * @ignore Passes in VDS color configuration.
   */
  colorConfig: PropTypes.object,

  /**
   * @ignore Passes in VDS icon configuration.
   */
  iconConfig: PropTypes.object
};
var defaultProps = {
  typographyType: 'body',
  viewport: 'desktop',
  typographySize: 'small',
  tooltipSize: 'small'
};
var ChildWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "ChildWrapper",
  componentId: "stn7mq-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  display: inline-flex;\n  align-items: center;\n"])));
var TooltipContent = /*#__PURE__*/styled.span.withConfig({
  displayName: "TooltipContent",
  componentId: "stn7mq-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  font-weight: 400;\n"])));
var StyledSpan = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledSpan",
  componentId: "stn7mq-2"
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  display: inline-block;\n  outline: none;\n"])));
var InlineWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "InlineWrapper",
  componentId: "stn7mq-3"
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  display: inline-flex;\n  vertical-align: middle;\n  position: relative;\n  top: ", "px;\n"])), function (_ref) {
  var type = _ref.type;
  return type === 'body' || type === 'micro' ? 0 : 1;
});

function _formatChildren(childArray) {
  var textWithoutIcon = '';
  childArray.map(function (item, index) {
    if (index !== childArray.length - 1) {
      textWithoutIcon += item + ' ';
    }
  });
  return textWithoutIcon;
}

function checkForNode(children) {
  var lastIndex = children.length - 1,
      //number of last index in child array
  lastChild = children[lastIndex],
      //last child in child array
  isNode = typeof children[lastIndex] !== 'string',
      //if last child is node
  isString = typeof children === 'string',
      //if children are a string
  firstNodes = [];
  if (children.length === undefined || children.length <= 1) return {
    childArray: [],
    textWithIcon: children
  }; //if only one child put with caret and return

  if (isNode || !isString) {
    //if the last child is node or mixture put last with the caret
    children.map(function (child, index) {
      //add all children to first nodes except the last one
      if (index !== lastIndex) firstNodes[index] = child;
    });
    return {
      childArray: firstNodes,
      textWithIcon: lastChild
    };
  } //if all children are "strings", put last word with caret


  var childArray = children.split(' ');
  var textWithIcon = childArray[childArray.length - 1];
  return {
    childArray: _formatChildren(childArray),
    textWithIcon: textWithIcon
  };
}

function _renderWithIcon(props) {
  var children = props.children,
      tooltipSize = props.tooltipSize,
      tooltipTitle = props.tooltipTitle,
      tooltipContent = props.tooltipContent,
      typographySize = props.typographySize,
      typographyType = props.typographyType,
      colorConfig = props.colorConfig,
      bodyConfig = props.bodyConfig,
      iconConfig = props.iconConfig,
      inverted = props.inverted,
      containerId = props.containerId,
      ariaLabel = props.ariaLabel;
  var childArray = children ? checkForNode(children).childArray : [];
  var textWithIcon = children ? checkForNode(children).textWithIcon : '';
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(StyledSpan, null, childArray, /*#__PURE__*/React.createElement(ChildWrapper, null, textWithIcon, /*#__PURE__*/React.createElement(InlineWrapper, {
    type: typographyType,
    size: typographySize
  }, /*#__PURE__*/React.createElement(Tooltip, {
    focusState: true,
    hoverState: true,
    activeState: true,
    iconConfig: iconConfig,
    bodyConfig: bodyConfig,
    colorConfig: colorConfig,
    typescale: "VDS",
    dialogPadding: calculateRem(12, 0, 12, 12),
    size: tooltipSize,
    title: tooltipTitle,
    inverted: inverted,
    containerId: containerId,
    ariaLabel: ariaLabel
  }, /*#__PURE__*/React.createElement(TooltipContent, null, tooltipContent))))));
}

function _calcTypographyComponent(props) {
  var Body = props.Body,
      Subtitle = props.Subtitle,
      Micro = props.Micro,
      Title = props.Title,
      typographyType = props.typographyType;
  var comp = Body;
  if (typographyType === 'title') comp = Title;
  if (typographyType === 'subtitle') comp = Subtitle;
  if (typographyType === 'micro') comp = Micro;
  return comp;
}

var TrailingTooltip = function TrailingTooltip(props) {
  var typographyPrimitive = props.typographyPrimitive,
      viewport = props.viewport,
      typographySize = props.typographySize,
      colorConfig = props.colorConfig,
      bold = props.bold,
      typographyColor = props.typographyColor,
      inverted = props.inverted,
      id = props.id;

  var TypographyComponent = _calcTypographyComponent(props);

  return /*#__PURE__*/React.createElement(TypographyComponent, {
    primitive: typographyPrimitive,
    viewport: viewport,
    size: typographySize,
    color: inverted ? colorConfig.white : typographyColor,
    bold: bold,
    id: id
  }, _renderWithIcon(props));
};

TrailingTooltip.defaultProps = defaultProps;
TrailingTooltip.propTypes = propTypes;
export default TrailingTooltip;